import React, { useEffect, useState } from "react";
import {
  Box, TextField, Button, Typography, Avatar, Snackbar, Alert,
  Divider, Grid, Chip,
} from "@mui/material";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { WalletsRequests } from "../../api/requests/Wallets/wallets";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

export default function Withdraw() {
  const [amount, setAmount] = useState("");
  const [walletBalance, setWalletBalance] = useState(0);
  const [totalCredit, setTotalCredit] = useState("0");
  const [totalWithdrawal, setTotalWithdrawal] = useState("0");
  const [recentTxns, setRecentTxns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchWalletData = async () => {
    try {
      const response = await WalletsRequests.getWalletHistory();
      const txns = response?.data || [];
      let credit = 0, withdrawal = 0;
      txns.forEach((t) => {
        if (t.type === "CREDIT") credit += parseFloat(t.amount);
        else if (t.type === "WITHDRAWAL") withdrawal += parseFloat(t.amount);
      });
      setTotalCredit(credit.toLocaleString("en-IN"));
      setTotalWithdrawal(withdrawal.toLocaleString("en-IN"));
      setWalletBalance(credit - withdrawal);
      setRecentTxns(txns.slice(0, 3));
    } catch (error) {}
  };

  useEffect(() => {
    fetchWalletData();
  }, []);

  const handleWithdraw = async (e) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);

    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      setSnackbar({ open: true, message: "Please enter a valid amount greater than 0.", severity: "warning" });
      return;
    }
    if (numAmount > walletBalance) {
      setSnackbar({ open: true, message: "Insufficient balance for this withdrawal.", severity: "error" });
      return;
    }

    setLoading(true);
    try {
      const response = await WalletsRequests.WithdrawalRequest(parseInt(numAmount));
      if (response) {
        setSnackbar({
          open: true,
          message: `Withdrawal of Rs. ${parseInt(numAmount).toLocaleString("en-IN")} submitted successfully!`,
          severity: "success",
        });
        setAmount("");
        fetchWalletData();
      } else {
        setSnackbar({ open: true, message: "Something went wrong, please try again.", severity: "error" });
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.response?.data || "An error occurred. Please try again later.";
      setSnackbar({ open: true, message: String(msg), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const numAmount = parseFloat(amount);
  const isValidAmount = amount && !isNaN(numAmount) && numAmount > 0;
  const remaining = isValidAmount ? walletBalance - numAmount : walletBalance;

  const features = [
    { icon: <SpeedIcon sx={{ fontSize: 20 }} />, title: "Fast Processing", desc: "24-48 hour processing time", color: "#059669", bg: "#ECFDF5" },
    { icon: <SecurityIcon sx={{ fontSize: 20 }} />, title: "Secure Transfer", desc: "Bank-level encryption", color: "#2563EB", bg: "#EFF6FF" },
    { icon: <SupportAgentIcon sx={{ fontSize: 20 }} />, title: "24/7 Support", desc: "Dedicated support team", color: "#0EA5E9", bg: "#F0F9FF" },
  ];

  return (
    <Box sx={{
      width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` },
      minHeight: "100vh", backgroundColor: "#F8FAFC",
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1000 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 22, color: "#059669" }} />
            <Typography sx={{ fontWeight: 700, color: "#0F172A", fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              Withdraw Funds
            </Typography>
          </Box>
          <Typography sx={{ color: "#64748B", fontSize: "13px" }}>
            Request a withdrawal from your wallet balance.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            {/* Balance Card */}
            <Box sx={{
              p: 3, borderRadius: "16px", mb: 3,
              backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0",
            }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.8px", mb: 0.5 }}>
                Available Balance
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: "28px", md: "34px" }, lineHeight: 1.2, mb: 1.5, color: "#059669" }}>
                Rs. {walletBalance.toLocaleString("en-IN")}
              </Typography>
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box>
                  <Typography sx={{ fontSize: "10px", color: "#94A3B8", textTransform: "uppercase" }}>Credits</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#059669" }}>Rs. {totalCredit}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "10px", color: "#94A3B8", textTransform: "uppercase" }}>Withdrawn</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#EF4444" }}>Rs. {totalWithdrawal}</Typography>
                </Box>
              </Box>
            </Box>

            {/* Withdrawal Form */}
            <Box
              component="form"
              onSubmit={handleWithdraw}
              sx={{
                backgroundColor: "#FFFFFF", p: 3, borderRadius: "12px",
                border: "1px solid #E2E8F0",
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#0F172A", mb: 2 }}>
                Withdrawal Amount
              </Typography>
              <TextField
                fullWidth variant="outlined" label="Amount (Rs.)" placeholder="Enter amount to withdraw"
                value={amount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || /^\d*\.?\d*$/.test(val)) setAmount(val);
                }}
                inputProps={{ inputMode: "numeric" }}
                sx={{
                  mb: 1.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px", fontSize: "16px",
                    "& fieldset": { borderColor: "#E2E8F0" },
                    "&:hover fieldset": { borderColor: "#059669" },
                    "&.Mui-focused fieldset": { borderColor: "#059669", borderWidth: "2px" },
                  },
                  "& .MuiInputLabel-root": { fontSize: "14px", color: "#64748B" },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#059669" },
                }}
              />
              {isValidAmount && (
                <Typography sx={{
                  fontSize: "12px", mb: 1.5,
                  color: remaining >= 0 ? "#64748B" : "#EF4444",
                  fontWeight: remaining < 0 ? 600 : 400,
                }}>
                  {remaining >= 0
                    ? `Remaining balance: Rs. ${remaining.toLocaleString("en-IN")}`
                    : "Insufficient balance!"}
                </Typography>
              )}
              <Button
                type="submit" fullWidth variant="contained"
                disabled={!isValidAmount || numAmount > walletBalance || loading}
                sx={{
                  backgroundColor: "#059669", fontWeight: 600,
                  fontSize: "15px", borderRadius: "10px", py: 1.5,
                  textTransform: "none", boxShadow: "none",
                  "&:hover": { backgroundColor: "#047857", boxShadow: "0 4px 16px rgba(5,150,105,0.3)" },
                  "&.Mui-disabled": { backgroundColor: "#E2E8F0", color: "#94A3B8" },
                }}
              >
                {loading ? "Processing..." : "Submit Withdrawal Request"}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            {/* Features */}
            <Box sx={{ backgroundColor: "#FFFFFF", borderRadius: "12px", border: "1px solid #E2E8F0", p: 3, mb: 3 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#0F172A", mb: 2 }}>
                Why Withdraw with FI?
              </Typography>
              {features.map((f, i) => (
                <Box key={i} sx={{ display: "flex", gap: 2, mb: i < features.length - 1 ? 2 : 0 }}>
                  <Avatar sx={{ width: 36, height: 36, backgroundColor: f.bg, color: f.color }}>
                    {f.icon}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#0F172A" }}>{f.title}</Typography>
                    <Typography sx={{ fontSize: "12px", color: "#64748B" }}>{f.desc}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Recent Activity */}
            <Box sx={{ backgroundColor: "#FFFFFF", borderRadius: "12px", border: "1px solid #E2E8F0", overflow: "hidden" }}>
              <Box sx={{ px: 3, py: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#0F172A" }}>
                  Recent Activity
                </Typography>
              </Box>
              <Divider sx={{ borderColor: "#F1F5F9" }} />
              {recentTxns.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography sx={{ color: "#CBD5E1", fontSize: "13px" }}>No recent transactions</Typography>
                </Box>
              ) : (
                recentTxns.map((txn, i) => {
                  const isCredit = txn.type === "CREDIT";
                  return (
                    <Box key={txn.id || i}>
                      <Box sx={{ px: 3, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar sx={{
                            width: 28, height: 28,
                            backgroundColor: isCredit ? "#D1FAE5" : "#FECDD3",
                            color: isCredit ? "#059669" : "#E11D48",
                          }}>
                            {isCredit ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />}
                          </Avatar>
                          <Box>
                            <Chip label={txn.type} size="small" sx={{
                              height: 18, fontSize: "9px", fontWeight: 600,
                              backgroundColor: isCredit ? "#D1FAE5" : "#FECDD3",
                              color: isCredit ? "#059669" : "#E11D48",
                              "& .MuiChip-label": { px: 0.6 },
                            }} />
                            <Typography sx={{ fontSize: "10px", color: "#94A3B8", mt: 0.2 }}>
                              {new Date(txn.timestamp).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "13px", color: isCredit ? "#059669" : "#E11D48" }}>
                          {isCredit ? "+" : "-"} Rs. {txn.amount}
                        </Typography>
                      </Box>
                      {i < recentTxns.length - 1 && <Divider sx={{ borderColor: "#F1F5F9" }} />}
                    </Box>
                  );
                })
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity} variant="filled"
          icon={snackbar.severity === "success" ? <CheckCircleOutlineIcon /> : undefined}
          sx={{ width: "100%", borderRadius: "10px", fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
